// DeleteConfirmationDialogContext.tsx
import React, { createContext, useContext, useState, ReactNode } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from "@mui/material";
import { theme } from "../theme";

interface WarningPopContextProps {
    openWarningPop: (onConfirm: () => void, message?: string) => void;
}

const WarningPopContext = createContext<WarningPopContextProps | undefined>(undefined);

export const useWarningPop = () => {
    const context = useContext(WarningPopContext);
    if (!context) {
        throw new Error("useWarningPop must be used within WarningPopContextProvider");
    }
    return context;
};

export const WarningPopContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [onConfirmCallback, setOnConfirmCallback] = useState<() => void>(() => { });
    const [message, setMessage] = useState("Are you sure you want to perform this action? This action cannot be undone.");

    const openWarningPop = (onConfirm: () => void, customMessage?: string) => {
        setOnConfirmCallback(() => onConfirm);
        setMessage(customMessage || "Are you sure you want to perform this action? This action cannot be undone.");
        setOpen(true);
    };

    const handleCancel = () => {
        setOpen(false);
    };

    const handleConfirm = async () => {
        try {
            await onConfirmCallback(); // ✅ await async deletes
        } finally {
            setOpen(false);
        }
    };

    return (
        <WarningPopContext.Provider value={{ openWarningPop }}>
            {children}

            <Dialog open={open} onClose={handleCancel}>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent>
                    <DialogContentText>{message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleCancel} sx={{ color: theme.palette.text.secondary }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm}
                        variant="contained"
                        sx={{
                            bgcolor: theme.palette.error.main,
                            '&:hover': {
                                bgcolor: theme.palette.error.dark,
                            },
                        }}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </WarningPopContext.Provider>
    );
};
